/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { QRCodeCanvas } from "qrcode.react";

import { DEFAULT_LANGUAGE_ARRAY, HEADER_LIMIT, MAIN_DOMAIN, PLATFORM } from "../../config";
import { getProjectTitle } from "../../assets/projectTitleHelper.js";
import { getProjectDescriptionText } from "../../assets/projectContentHelper.js";
import { formatCurrencyAmount as formatAmount, formatDateYMD as formatDate } from "../../assets/helper.js";
import ProjectDescriptionEditor from "./ProjectDescriptionEditor";
import MediaGalleryModal from "./MediaGalleryModal";
import ExportMenu from "./ExportMenu";
import PaymentMethodModal from "../OrgSettings/component/PaymentMethodModal";

import "./ProjectTable.css";

const DESCRIPTION_LNG_ARR = [...DEFAULT_LANGUAGE_ARRAY, "default"];
const DESCRIPTION_PREVIEW_LENGTH = 120;
const QR_STATUSES = ["open", "unlisted"];

const DEFAULT_TITLE_COL_WIDTH = 340;
const DEFAULT_DESCRIPTION_COL_WIDTH = 320;
const MIN_RESIZABLE_COL_WIDTH = 120;
const MAX_RESIZABLE_COL_WIDTH = 640;
const BULK_CHIP_LIMIT = 5;

const STATUS_OPTIONS = ["open", "unlisted", "closed"];
// Business (Plug) projects are always payment type — the app hides the
// toggle everywhere, so the table hides the Type column too.
const SHOW_TYPE = PLATFORM !== "plug";
// Sum of every column's fixed width except Title/Description (which are
// user-resizable) — table-layout:fixed needs an explicit width for every
// column, so this anchors the table's min-width to avoid columns being
// squeezed smaller than intended when the viewport is narrow.
const OTHER_COLUMNS_WIDTH = 40 + 80 + 80 + 130 + (SHOW_TYPE ? 130 : 0) + 110 + 100 + 80 + 110 + 120 + 120 + 120 + 56;
// ---- row windowing (CP-230) ----
// Below this many missions the table renders every row, exactly as before.
// Above it, only rows near the viewport render; runs of hidden rows become
// spacer <tr>s carrying their combined height, so scroll geometry (and the
// sticky header/columns) is preserved without a virtualization library —
// react-window would have meant rebuilding the colgroup, sticky columns and
// column resizing as a div grid.
const WINDOW_THRESHOLD = 60;
const WINDOW_OVERSCAN = 8;
// Fallbacks for before the first row/viewport has been measured (and for
// jsdom, which reports zero heights).
const WINDOW_ROW_HEIGHT = 64;
const WINDOW_VIEWPORT_HEIGHT = 600;

const STATUS_COLORS = {
  open: "#94D365",
  unlisted: "#F5A623",
  closed: "#AAAAAA",
};

const LANG_LABEL_FALLBACKS = {
  en: "English",
  zh: "繁體中文",
  cn: "简体中文",
};

const FALLBACKS = {
  clickToEdit: "Click to edit",
  titleRequired: "Title is required.",
  invalidAmount: "Target must be a non-negative number.",
  selected: "{{n}} selected",
  selectFieldToUpdate: "Choose at least one field to update.",
  bulkPreviewLabel: "Will update {{n}} project(s):",
  bulkPreviewEmpty: "Select a field below to preview the update.",
  removeFromSelection: "Remove from selection",
  moreSelected: "+{{n}} more",
  apply: "Apply",
  applying: "Applying...",
  clear: "Clear",
  save: "Save",
  cancel: "Cancel",
  allLanguages: "All languages",
  langEn: "English",
  langZh: "繁體中文",
  langCn: "简体中文",
  thTitle: "Title",
  thDescription: "Description",
  thImage: "Image",
  imagePickerTitle: "Choose Image",
  addImage: "Add image",
  thQrCode: "QR Code",
  viewPage: "View Page",
  thDonorCount: "Donors",
  thTransactionCount: "Transactions",
  thAverageDonation: "Avg. Donation",
  thFirstDonation: "First Donation",
  thLastDonation: "Last Donation",
  thStatus: "Status",
  thType: "Type",
  thTarget: "Target",
  thRaised: "Raised",
  thEdit: "Edit",
  saving: "Saving...",
  statusOpen: "Public",
  statusUnlisted: "Unlisted",
  statusClosed: "Closed",
  typeDonation: "Donation",
  typePayment: "Payment",
  empty: "No projects to show.",
  loading: "Loading projects...",
  updateFailed: "Update failed. Please try again.",
};

const interpolate = (str, vars) => {
  if (!str || !vars) return str;
  let out = str;
  Object.keys(vars).forEach((k) => {
    out = out.replace(new RegExp("\\{\\{" + k + "\\}\\}", "g"), vars[k]);
  });
  return out;
};

const projectUrl = (subDomain, projectId) =>
  "https://" + subDomain + "." + MAIN_DOMAIN + "/MissionDetails?no=" + projectId;

const canShowQr = (mission, subDomain) =>
  !!subDomain && QR_STATUSES.indexOf(mission.status) !== -1;

const headerImages = (mission) => {
  const raw = mission && mission.headerBackgroundPicture;
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : String(raw).split(",");
  return list.map((url) => url.trim()).filter(Boolean);
};

const descriptionPreview = (mission, language) => {
  // The list endpoint sends short plain-text previews per language
  // (CP-229) so the client no longer parses full Draft blobs in render.
  const server = mission && mission.descriptionPreview;
  let text;
  if (server && typeof server === "object") {
    text = server[language] || server.default || "";
  } else {
    // Old backend during the deploy window: the list still carries the full
    // description and no preview — keep the legacy client-side compute.
    text = getProjectDescriptionText(
      language,
      mission.description,
      DESCRIPTION_LNG_ARR
    );
  }
  if (!text) return "";
  const collapsed = text.replace(/\s+/g, " ").trim();
  return collapsed.length > DESCRIPTION_PREVIEW_LENGTH
    ? collapsed.slice(0, DESCRIPTION_PREVIEW_LENGTH).trim() + "…"
    : collapsed;
};

const hasTitleI18n = (mission) => {
  const i18n = mission && mission.titleI18n;
  if (!i18n || typeof i18n !== "object") return false;
  return DEFAULT_LANGUAGE_ARRAY.some(
    (lng) => typeof i18n[lng] === "string" && i18n[lng].trim().length > 0
  );
};

const seedTitleValues = (mission, language) => {
  const i18n =
    mission.titleI18n && typeof mission.titleI18n === "object"
      ? mission.titleI18n
      : {};
  const fallback =
    getProjectTitle(language, mission) || mission.title || "";
  const values = {};
  DEFAULT_LANGUAGE_ARRAY.forEach((lng) => {
    if (typeof i18n[lng] === "string") {
      values[lng] = i18n[lng];
    } else {
      values[lng] = fallback;
    }
  });
  return values;
};

const orderedTitleLanguages = (language) => {
  if (!language || DEFAULT_LANGUAGE_ARRAY.indexOf(language) === -1) {
    return DEFAULT_LANGUAGE_ARRAY.slice();
  }
  return [
    language,
    ...DEFAULT_LANGUAGE_ARRAY.filter((lng) => lng !== language),
  ];
};

class ProjectTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedIds: {},
      bulkIncludeStatus: true,
      bulkIncludeType: false,
      bulkIncludeTarget: false,
      bulkStatus: "open",
      bulkType: "donation",
      bulkTarget: "",
      savingIds: {},
      bulkSaving: false,
      error: "",
      editing: null,
      descriptionEditId: null,
      imagePickerId: null,
      qrViewId: null,
      titleColWidth: DEFAULT_TITLE_COL_WIDTH,
      descriptionColWidth: DEFAULT_DESCRIPTION_COL_WIDTH,
      windowScrollTop: 0,
    };
    this.resizeState = null;
    this.tableWrapRef = React.createRef();
    this.measuredRowHeight = null;
    this.lastWindowRange = null;
    this.measureRow = this.measureRow.bind(this);
    this.handleTableScroll = this.handleTableScroll.bind(this);
    this.toggleAll = this.toggleAll.bind(this);
    this.handleBulkApply = this.handleBulkApply.bind(this);
    this.handleClearSelection = this.handleClearSelection.bind(this);
    this.handleBulkFieldToggle = this.handleBulkFieldToggle.bind(this);
    this.handleBulkStatusSelect = this.handleBulkStatusSelect.bind(this);
    this.handleBulkTypeSelect = this.handleBulkTypeSelect.bind(this);
    this.handleBulkTargetInput = this.handleBulkTargetInput.bind(this);
    this.handleEditInput = this.handleEditInput.bind(this);
    this.cancelEdit = this.cancelEdit.bind(this);
    this.expandToMultiTitle = this.expandToMultiTitle.bind(this);
    this.closeDescriptionEditor = this.closeDescriptionEditor.bind(this);
    this.closeImagePicker = this.closeImagePicker.bind(this);
    this.closeQrView = this.closeQrView.bind(this);
    this.langLabel = this.langLabel.bind(this);
    this.handleResizeStart = this.handleResizeStart.bind(this);
    this.handleResizeMove = this.handleResizeMove.bind(this);
    this.handleResizeEnd = this.handleResizeEnd.bind(this);
  }

  componentWillUnmount() {
    this.handleResizeEnd();
  }

  handleResizeStart(column, event) {
    event.preventDefault();
    const point = event.touches ? event.touches[0] : event;
    this.resizeState = {
      column,
      startX: point.clientX,
      startWidth:
        column === "title"
          ? this.state.titleColWidth
          : this.state.descriptionColWidth,
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", this.handleResizeMove);
    document.addEventListener("mouseup", this.handleResizeEnd);
    document.addEventListener("touchmove", this.handleResizeMove, {
      passive: false,
    });
    document.addEventListener("touchend", this.handleResizeEnd);
  }

  handleResizeMove(event) {
    if (!this.resizeState) return;
    if (event.touches) event.preventDefault();
    const point = event.touches ? event.touches[0] : event;
    const delta = point.clientX - this.resizeState.startX;
    const next = Math.min(
      MAX_RESIZABLE_COL_WIDTH,
      Math.max(MIN_RESIZABLE_COL_WIDTH, this.resizeState.startWidth + delta)
    );
    if (this.resizeState.column === "title") {
      this.setState({ titleColWidth: next });
    } else {
      this.setState({ descriptionColWidth: next });
    }
  }

  handleResizeEnd() {
    if (!this.resizeState) return;
    this.resizeState = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", this.handleResizeMove);
    document.removeEventListener("mouseup", this.handleResizeEnd);
    document.removeEventListener("touchmove", this.handleResizeMove);
    document.removeEventListener("touchend", this.handleResizeEnd);
  }

  t = (key, vars) => {
    const block =
      this.props.languageObj && typeof this.props.languageObj === "object"
        ? this.props.languageObj
        : {};
    return interpolate(block[key] || FALLBACKS[key] || "", vars);
  };

  langLabel(lng) {
    if (lng === "en") return this.t("langEn") || LANG_LABEL_FALLBACKS.en;
    if (lng === "zh") return this.t("langZh") || LANG_LABEL_FALLBACKS.zh;
    if (lng === "cn") return this.t("langCn") || LANG_LABEL_FALLBACKS.cn;
    return LANG_LABEL_FALLBACKS[lng] || lng;
  }

  statusLabel(status) {
    if (status === "unlisted") return this.t("statusUnlisted");
    if (status === "closed") return this.t("statusClosed");
    return this.t("statusOpen");
  }

  selectedIdList() {
    const { selectedIds } = this.state;
    return Object.keys(selectedIds)
      .filter((id) => selectedIds[id])
      .map(Number);
  }

  toggleRow(id) {
    this.setState((prev) => ({
      selectedIds: { ...prev.selectedIds, [id]: !prev.selectedIds[id] },
      error: "",
    }));
  }

  toggleAll() {
    const { missions } = this.props;
    const allSelected =
      missions.length > 0 &&
      missions.every((m) => this.state.selectedIds[m.id]);
    const selectedIds = {};
    if (!allSelected) {
      missions.forEach((m) => {
        selectedIds[m.id] = true;
      });
    }
    this.setState({ selectedIds, error: "" });
  }

  handleClearSelection() {
    this.setState({ selectedIds: {}, error: "" });
  }

  handleBulkFieldToggle(field) {
    const key =
      field === "status"
        ? "bulkIncludeStatus"
        : field === "type"
          ? "bulkIncludeType"
          : "bulkIncludeTarget";
    this.setState((prev) => ({ [key]: !prev[key], error: "" }));
  }

  handleBulkStatusSelect(e) {
    this.setState({ bulkStatus: e.target.value, bulkIncludeStatus: true });
  }

  handleBulkTypeSelect(e) {
    this.setState({ bulkType: e.target.value, bulkIncludeType: true });
  }

  handleBulkTargetInput(e) {
    this.setState({ bulkTarget: e.target.value, bulkIncludeTarget: true });
  }

  handleRowStatusChange(mission, e) {
    const status = e.target.value;
    if (status === mission.status) return;
    this.setState((prev) => ({
      // Track which field is saving so the spinner renders in that cell
      savingIds: { ...prev.savingIds, [mission.id]: "status" },
      error: "",
    }));
    this.props
      .onStatusChange(mission, status)
      .then((ok) => {
        this.setState((prev) => {
          const savingIds = { ...prev.savingIds };
          delete savingIds[mission.id];
          return { savingIds, error: ok ? "" : this.t("updateFailed") };
        });
      })
      .catch(() => {
        this.setState((prev) => {
          const savingIds = { ...prev.savingIds };
          delete savingIds[mission.id];
          return { savingIds, error: this.t("updateFailed") };
        });
      });
  }

  handleRowTypeChange(mission, e) {
    const donation = e.target.value === "donation";
    if (donation === !!mission.donation) return;
    this.setState((prev) => ({
      savingIds: { ...prev.savingIds, [mission.id]: "donation" },
      error: "",
    }));
    this.props
      .onFieldSave(mission, { donation })
      .then((ok) => {
        this.setState((prev) => {
          const savingIds = { ...prev.savingIds };
          delete savingIds[mission.id];
          return { savingIds, error: ok ? "" : this.t("updateFailed") };
        });
      })
      .catch(() => {
        this.setState((prev) => {
          const savingIds = { ...prev.savingIds };
          delete savingIds[mission.id];
          return { savingIds, error: this.t("updateFailed") };
        });
      });
  }

  startEdit(mission, field) {
    if (this.state.bulkSaving || this.state.savingIds[mission.id]) return;

    if (field === "title") {
      if (hasTitleI18n(mission)) {
        this.setState({
          editing: {
            id: mission.id,
            field: "title",
            mode: "multi",
            values: seedTitleValues(mission, this.props.language),
          },
          error: "",
        });
        return;
      }
      this.setState({
        editing: {
          id: mission.id,
          field: "title",
          mode: "single",
          value: getProjectTitle(this.props.language, mission) || "",
        },
        error: "",
      });
      return;
    }

    const value =
      mission.targetAmount || mission.targetAmount === 0
        ? String(mission.targetAmount)
        : "";
    this.setState({
      editing: { id: mission.id, field, mode: "single", value },
      error: "",
    });
  }

  expandToMultiTitle(mission) {
    const { editing } = this.state;
    if (!editing || editing.id !== mission.id || editing.field !== "title") {
      return;
    }
    const seed = seedTitleValues(mission, this.props.language);
    const currentLang = this.props.language;
    if (editing.mode === "single" && currentLang && editing.value != null) {
      seed[currentLang] = editing.value;
    }
    this.setState({
      editing: {
        id: mission.id,
        field: "title",
        mode: "multi",
        values: seed,
      },
      error: "",
    });
  }

  buildTitleSaveFields(mission, trimmed) {
    const language = this.props.language;
    const existingI18n =
      mission.titleI18n && typeof mission.titleI18n === "object"
        ? mission.titleI18n
        : null;
    // Keep titleI18n in sync for the active language so the list view
    // reflects the edit (getProjectTitle prefers titleI18n over title).
    if (existingI18n && language && existingI18n[language]) {
      return {
        title: trimmed,
        titleI18n: { ...existingI18n, [language]: trimmed },
      };
    }
    return { title: trimmed };
  }

  handleEditInput(e) {
    const { editing } = this.state;
    if (!editing) return;
    if (editing.mode === "multi") {
      const lng = e.target.name;
      this.setState({
        editing: {
          ...editing,
          values: { ...editing.values, [lng]: e.target.value },
        },
      });
      return;
    }
    this.setState({
      editing: { ...editing, value: e.target.value },
    });
  }

  cancelEdit() {
    this.setState({ editing: null });
  }

  toggleDescriptionEditor(mission) {
    this.setState((prev) => ({
      descriptionEditId: prev.descriptionEditId === mission.id ? null : mission.id,
      editing: null,
    }));
  }

  closeDescriptionEditor() {
    this.setState({ descriptionEditId: null });
  }

  openImagePicker(mission) {
    this.setState({ imagePickerId: mission.id });
  }

  closeImagePicker() {
    this.setState({ imagePickerId: null });
  }

  handleImageSelect(mission, url) {
    const existing = headerImages(mission);
    if (existing.length >= HEADER_LIMIT) {
      this.closeImagePicker();
      return;
    }
    this.closeImagePicker();
    this.saveField(mission, { headerBackgroundPicture: existing.concat(url) });
  }

  openQrView(mission) {
    this.setState({ qrViewId: mission.id });
  }

  closeQrView() {
    this.setState({ qrViewId: null });
  }

  handleEditKeyDown(e, mission) {
    const { editing } = this.state;
    if (e.key === "Escape") {
      this.cancelEdit();
      return;
    }
    if (e.key === "Enter" && editing && editing.mode !== "multi") {
      this.commitEdit(mission);
    }
  }

  commitMultiTitle(mission) {
    const { editing } = this.state;
    if (!editing || editing.id !== mission.id || editing.mode !== "multi") {
      return;
    }
    const trimmed = {};
    DEFAULT_LANGUAGE_ARRAY.forEach((lng) => {
      trimmed[lng] = String(
        (editing.values && editing.values[lng]) || ""
      ).trim();
    });
    const hasAny = DEFAULT_LANGUAGE_ARRAY.some((lng) => trimmed[lng]);
    if (!hasAny) {
      this.setState({ error: this.t("titleRequired") });
      return;
    }
    const language = this.props.language;
    const title =
      (language && trimmed[language]) ||
      DEFAULT_LANGUAGE_ARRAY.map((lng) => trimmed[lng]).find(Boolean) ||
      "";

    const existing = seedTitleValues(mission, language);
    const unchanged = DEFAULT_LANGUAGE_ARRAY.every(
      (lng) => trimmed[lng] === String(existing[lng] || "").trim()
    );
    if (unchanged) {
      this.setState({ editing: null });
      return;
    }

    this.saveField(mission, { title, titleI18n: trimmed });
  }

  commitEdit(mission) {
    const { editing } = this.state;
    if (!editing || editing.id !== mission.id) return;

    if (editing.field === "title") {
      if (editing.mode === "multi") {
        this.commitMultiTitle(mission);
        return;
      }
      const trimmed = String(editing.value).trim();
      if (!trimmed) {
        this.setState({ editing: null, error: this.t("titleRequired") });
        return;
      }
      const currentTitle = getProjectTitle(this.props.language, mission) || "";
      if (trimmed === currentTitle) {
        this.setState({ editing: null });
        return;
      }
      this.saveField(mission, this.buildTitleSaveFields(mission, trimmed));
      return;
    }

    const raw = String(editing.value).trim().replace(/,/g, "");
    const num = raw === "" ? 0 : Number(raw);
    if (!isFinite(num) || num < 0) {
      this.setState({ editing: null, error: this.t("invalidAmount") });
      return;
    }
    if (num === Number(mission.targetAmount || 0)) {
      this.setState({ editing: null });
      return;
    }
    this.saveField(mission, { targetAmount: num });
  }

  saveField(mission, fields) {
    const field = Object.keys(fields)[0];
    this.setState((prev) => ({
      editing: null,
      savingIds: { ...prev.savingIds, [mission.id]: field },
    }));
    this.props
      .onFieldSave(mission, fields)
      .then((ok) => {
        this.setState((prev) => {
          const savingIds = { ...prev.savingIds };
          delete savingIds[mission.id];
          return { savingIds, error: ok ? "" : this.t("updateFailed") };
        });
      })
      .catch(() => {
        this.setState((prev) => {
          const savingIds = { ...prev.savingIds };
          delete savingIds[mission.id];
          return { savingIds, error: this.t("updateFailed") };
        });
      });
  }

  renderMultiTitleEditor(mission) {
    const { editing } = this.state;
    const values = (editing && editing.values) || {};
    const langs = orderedTitleLanguages(this.props.language);
    return (
      <div
        className="ProjectTable-titleEditor"
        onKeyDown={(e) => this.handleEditKeyDown(e, mission)}
      >
        {langs.map((lng, index) => (
          <label key={lng} className="ProjectTable-titleLangRow">
            <span className="ProjectTable-titleLangLabel">
              {this.langLabel(lng)}
            </span>
            <input
              className="ProjectTable-editInput"
              name={lng}
              type="text"
              value={values[lng] || ""}
              onChange={this.handleEditInput}
              ref={index === 0 ? (el) => el && el.focus() : undefined}
            />
          </label>
        ))}
        <div className="ProjectTable-titleEditorActions">
          <button
            type="button"
            className="ProjectTable-linkBtn"
            onClick={this.cancelEdit}
          >
            {this.t("cancel")}
          </button>
          <button
            type="button"
            className="ProjectTable-applyBtn"
            onClick={() => this.commitMultiTitle(mission)}
          >
            {this.t("save")}
          </button>
        </div>
      </div>
    );
  }

  renderSingleTitleEditor(mission) {
    const { editing } = this.state;
    const showAllLanguages =
      this.props.canUseMultiLanguage || hasTitleI18n(mission);
    return (
      <div className="ProjectTable-singleTitleEdit">
        <input
          className="ProjectTable-editInput"
          type="text"
          value={editing.value}
          onChange={this.handleEditInput}
          onBlur={() => this.commitEdit(mission)}
          onKeyDown={(e) => this.handleEditKeyDown(e, mission)}
          ref={(el) => el && el.focus()}
        />
        {showAllLanguages ? (
          <button
            type="button"
            className="ProjectTable-linkBtn ProjectTable-allLangBtn"
            // Prevent input blur-commit from racing the expand click
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => this.expandToMultiTitle(mission)}
          >
            {this.t("allLanguages")}
          </button>
        ) : null}
      </div>
    );
  }

  renderEditableCell(mission, field, display) {
    const { editing, savingIds, bulkSaving } = this.state;
    const isEditing =
      editing && editing.id === mission.id && editing.field === field;

    if (isEditing && field === "title") {
      if (editing.mode === "multi") {
        return this.renderMultiTitleEditor(mission);
      }
      return this.renderSingleTitleEditor(mission);
    }

    if (isEditing) {
      return (
        <input
          className="ProjectTable-editInput"
          type={field === "targetAmount" ? "number" : "text"}
          min={field === "targetAmount" ? 0 : undefined}
          value={editing.value}
          onChange={this.handleEditInput}
          onBlur={() => this.commitEdit(mission)}
          onKeyDown={(e) => this.handleEditKeyDown(e, mission)}
          ref={(el) => el && el.focus()}
        />
      );
    }
    const saving = !!savingIds[mission.id];
    return (
      <span
        className="ProjectTable-editable"
        onClick={
          saving || bulkSaving
            ? undefined
            : () => this.startEdit(mission, field)
        }
        title={this.t("clickToEdit")}
      >
        {display}
        {savingIds[mission.id] === field ? (
          <span className="ProjectTable-spinner" />
        ) : null}
      </span>
    );
  }

  buildBulkFields() {
    const {
      bulkIncludeStatus,
      bulkIncludeType,
      bulkIncludeTarget,
      bulkStatus,
      bulkType,
      bulkTarget,
    } = this.state;
    if (!bulkIncludeStatus && !bulkIncludeType && !bulkIncludeTarget) {
      return { error: this.t("selectFieldToUpdate") };
    }
    const fields = {};
    if (bulkIncludeStatus) {
      fields.status = bulkStatus;
    }
    if (bulkIncludeType) {
      fields.donation = bulkType === "donation";
    }
    if (bulkIncludeTarget) {
      const raw = String(bulkTarget).trim().replace(/,/g, "");
      const num = raw === "" ? NaN : Number(raw);
      if (!isFinite(num) || num < 0) {
        return { error: this.t("invalidAmount") };
      }
      fields.targetAmount = num;
    }
    return { fields };
  }

  handleBulkApply() {
    const ids = this.selectedIdList();
    if (!ids.length || this.state.bulkSaving) return;
    const built = this.buildBulkFields();
    if (built.error) {
      this.setState({ error: built.error });
      return;
    }
    this.setState({ bulkSaving: true, error: "" });
    this.props
      .onBulkUpdate(ids, built.fields)
      .then((ok) => {
        this.setState({
          bulkSaving: false,
          selectedIds: ok ? {} : this.state.selectedIds,
          error: ok ? "" : this.t("updateFailed"),
        });
      })
      .catch(() => {
        this.setState({ bulkSaving: false, error: this.t("updateFailed") });
      });
  }

  buildBulkPreviewParts() {
    const {
      bulkIncludeStatus,
      bulkIncludeType,
      bulkIncludeTarget,
      bulkStatus,
      bulkType,
      bulkTarget,
    } = this.state;
    const parts = [];
    if (bulkIncludeStatus) {
      parts.push(`${this.t("thStatus")}: ${this.statusLabel(bulkStatus)}`);
    }
    if (SHOW_TYPE && bulkIncludeType) {
      parts.push(
        `${this.t("thType")}: ${
          bulkType === "donation" ? this.t("typeDonation") : this.t("typePayment")
        }`
      );
    }
    if (bulkIncludeTarget) {
      parts.push(`${this.t("thTarget")}: ${bulkTarget === "" ? "0" : bulkTarget}`);
    }
    return parts;
  }

  renderBulkBar() {
    const ids = this.selectedIdList();
    if (!ids.length) return null;
    const {
      bulkSaving,
      bulkIncludeStatus,
      bulkIncludeType,
      bulkIncludeTarget,
      bulkStatus,
      bulkType,
      bulkTarget,
    } = this.state;
    const { selectedIds } = this.state;
    const selectedMissions = this.props.missions.filter((m) => selectedIds[m.id]);
    const visibleChips = selectedMissions.slice(0, BULK_CHIP_LIMIT);
    const extraChipCount = selectedMissions.length - visibleChips.length;
    const previewParts = this.buildBulkPreviewParts();

    return (
      <div className="ProjectTable-bulkBar">
        <div className="ProjectTable-bulkHeader">
          <span className="ProjectTable-bulkCount">
            {this.t("selected", { n: ids.length })}
          </span>
          <div className="ProjectTable-bulkChips">
            {visibleChips.map((mission) => (
              <span className="ProjectTable-bulkChip" key={mission.id}>
                <span className="ProjectTable-bulkChipLabel">
                  {getProjectTitle(this.props.language, mission)}
                </span>
                <button
                  type="button"
                  className="ProjectTable-bulkChipRemove"
                  onClick={() => this.toggleRow(mission.id)}
                  disabled={bulkSaving}
                  aria-label={this.t("removeFromSelection")}
                >
                  ×
                </button>
              </span>
            ))}
            {extraChipCount > 0 ? (
              <span className="ProjectTable-bulkChipMore">
                {this.t("moreSelected", { n: extraChipCount })}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            className="ProjectTable-linkBtn"
            onClick={this.handleClearSelection}
            disabled={bulkSaving}
          >
            {this.t("clear")}
          </button>
        </div>

        <div className="ProjectTable-bulkFields">
          <label
            className={`ProjectTable-bulkField${
              bulkIncludeStatus ? "" : " ProjectTable-bulkField--inactive"
            }`}
          >
            <input
              type="checkbox"
              checked={bulkIncludeStatus}
              onChange={() => this.handleBulkFieldToggle("status")}
              disabled={bulkSaving}
            />
            <span className="ProjectTable-bulkLabel">{this.t("thStatus")}</span>
            <select
              className="ProjectTable-statusSelect"
              value={bulkStatus}
              onChange={this.handleBulkStatusSelect}
              disabled={bulkSaving || !bulkIncludeStatus}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {this.statusLabel(s)}
                </option>
              ))}
            </select>
          </label>

          {SHOW_TYPE ? (
            <label
              className={`ProjectTable-bulkField${
                bulkIncludeType ? "" : " ProjectTable-bulkField--inactive"
              }`}
            >
              <input
                type="checkbox"
                checked={bulkIncludeType}
                onChange={() => this.handleBulkFieldToggle("type")}
                disabled={bulkSaving}
              />
              <span className="ProjectTable-bulkLabel">{this.t("thType")}</span>
              <select
                className="ProjectTable-statusSelect"
                value={bulkType}
                onChange={this.handleBulkTypeSelect}
                disabled={bulkSaving || !bulkIncludeType}
              >
                <option value="donation">{this.t("typeDonation")}</option>
                <option value="payment">{this.t("typePayment")}</option>
              </select>
            </label>
          ) : null}

          <label
            className={`ProjectTable-bulkField${
              bulkIncludeTarget ? "" : " ProjectTable-bulkField--inactive"
            }`}
          >
            <input
              type="checkbox"
              checked={bulkIncludeTarget}
              onChange={() => this.handleBulkFieldToggle("target")}
              disabled={bulkSaving}
            />
            <span className="ProjectTable-bulkLabel">{this.t("thTarget")}</span>
            <input
              className="ProjectTable-bulkTargetInput"
              type="number"
              min={0}
              value={bulkTarget}
              onChange={this.handleBulkTargetInput}
              disabled={bulkSaving || !bulkIncludeTarget}
              placeholder="0"
            />
          </label>
        </div>

        <div className="ProjectTable-bulkFooter">
          <span className="ProjectTable-bulkPreview">
            {previewParts.length > 0 ? (
              <React.Fragment>
                <span className="ProjectTable-bulkPreviewLabel">
                  {this.t("bulkPreviewLabel", { n: ids.length })}
                </span>{" "}
                {previewParts.join("  •  ")}
              </React.Fragment>
            ) : (
              this.t("bulkPreviewEmpty")
            )}
          </span>
          <button
            type="button"
            className="ProjectTable-applyBtn"
            onClick={this.handleBulkApply}
            disabled={bulkSaving}
          >
            {bulkSaving ? (
              <span>
                <span className="ProjectTable-spinner ProjectTable-spinner--light" />
                {this.t("applying")}
              </span>
            ) : (
              this.t("apply")
            )}
          </button>
        </div>
      </div>
    );
  }

  handleRowOpen(mission, event) {
    if (!this.props.onProjectClick) return;
    // Ignore clicks on interactive controls so edit/bulk UX stays intact.
    const interactive = event.target.closest(
      "input, select, button, .ProjectTable-editable, .ProjectTable-editInput, .ProjectTable-titleEditor, .ProjectTable-singleTitleEdit, .ProjectTable-linkBtn, .ProjectTable-imageTrigger, .ProjectTable-qrTrigger"
    );
    if (interactive) return;
    this.props.onProjectClick(mission);
  }

  // Ref callback on the first rendered data row: use its real height for the
  // spacer math instead of the WINDOW_ROW_HEIGHT guess.
  measureRow(el) {
    if (!el || this.measuredRowHeight) return;
    const h = el.getBoundingClientRect().height;
    if (h > 0) {
      this.measuredRowHeight = h;
      if (Math.abs(h - WINDOW_ROW_HEIGHT) > 4) this.forceUpdate();
    }
  }

  windowRowHeight() {
    return this.measuredRowHeight || WINDOW_ROW_HEIGHT;
  }

  windowViewportHeight() {
    const wrap = this.tableWrapRef.current;
    const h = wrap ? wrap.clientHeight : 0;
    return h > 0 ? h : WINDOW_VIEWPORT_HEIGHT;
  }

  // Indices [start, end) of the rows that should be in the DOM for the
  // current scroll position.
  windowRange(missionCount, scrollTop) {
    const rowH = this.windowRowHeight();
    // Clamp against a stale scrollTop from before a filter shrank the list
    // (real browsers re-clamp scrollTop themselves; this covers the frame in
    // between).
    const start = Math.min(
      Math.max(0, Math.floor(scrollTop / rowH) - WINDOW_OVERSCAN),
      Math.max(0, missionCount - 1)
    );
    const end = Math.min(
      missionCount,
      Math.ceil((scrollTop + this.windowViewportHeight()) / rowH) +
        WINDOW_OVERSCAN
    );
    return [start, end];
  }

  handleTableScroll(event) {
    if (this.props.missions.length <= WINDOW_THRESHOLD) return;
    const scrollTop = event.target.scrollTop;
    const range = this.windowRange(this.props.missions.length, scrollTop);
    const last = this.lastWindowRange;
    // Re-render only when the window actually moves, not on every pixel.
    if (last && last[0] === range[0] && last[1] === range[1]) return;
    this.lastWindowRange = range;
    this.setState({ windowScrollTop: scrollTop });
  }

  columnCount() {
    return SHOW_TYPE ? 15 : 14;
  }

  renderSpacerRow(key, rowCount) {
    if (rowCount <= 0) return null;
    return (
      <tr key={key} aria-hidden="true" className="ProjectTable-windowSpacer">
        <td
          colSpan={this.columnCount()}
          style={{
            height: rowCount * this.windowRowHeight(),
            padding: 0,
            border: 0,
          }}
        />
      </tr>
    );
  }

  // The full body when windowed: window rows plus always-rendered "pinned"
  // rows — an open description editor or an inline edit must never unmount
  // with the user's changes just because the row scrolled out of view.
  renderWindowedBody(missions) {
    const [start, end] = this.windowRange(
      missions.length,
      this.state.windowScrollTop
    );
    this.lastWindowRange = [start, end];
    const pinned = new Set();
    const { descriptionEditId, editing } = this.state;
    missions.forEach((m, i) => {
      if (
        (descriptionEditId != null && m.id === descriptionEditId) ||
        (editing && m.id === editing.id)
      ) {
        pinned.add(i);
      }
    });

    const out = [];
    let hiddenRun = 0;
    let renderedFirst = false;
    for (let i = 0; i < missions.length; i++) {
      const visible = (i >= start && i < end) || pinned.has(i);
      if (!visible) {
        hiddenRun += 1;
        continue;
      }
      if (hiddenRun > 0) {
        out.push(this.renderSpacerRow("spacer-" + i, hiddenRun));
        hiddenRun = 0;
      }
      out.push(this.renderRow(missions[i], !renderedFirst));
      renderedFirst = true;
    }
    if (hiddenRun > 0) {
      out.push(this.renderSpacerRow("spacer-tail", hiddenRun));
    }
    return out;
  }

  renderRow(mission, measureFirst) {
    const { selectedIds, savingIds, bulkSaving, descriptionEditId } = this.state;
    const saving = !!savingIds[mission.id];
    const selected = !!selectedIds[mission.id];
    const canOpen = typeof this.props.onProjectClick === "function";
    const rowClassNames = [
      canOpen ? "ProjectTable-row--clickable" : "",
      saving || (bulkSaving && selected) ? "ProjectTable-row--saving" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const columnCount = SHOW_TYPE ? 15 : 14;
    const canEditDescription =
      this.props.canUseMultiLanguage || hasTitleI18n(mission);
    const previewText = descriptionPreview(mission, this.props.language);
    const images = headerImages(mission);
    const showQr = canShowQr(mission, this.props.subDomain);
    return (
      <React.Fragment key={mission.id}>
        <tr
          ref={measureFirst ? this.measureRow : undefined}
          className={rowClassNames || undefined}
          onClick={
            canOpen ? (event) => this.handleRowOpen(mission, event) : undefined
          }
        >
          <td className="ProjectTable-checkCell">
            <input
              type="checkbox"
              checked={!!selectedIds[mission.id]}
              onChange={() => this.toggleRow(mission.id)}
              aria-label={getProjectTitle(this.props.language, mission)}
            />
          </td>
          <td className="ProjectTable-titleCell">
            {this.renderEditableCell(
              mission,
              "title",
              getProjectTitle(this.props.language, mission)
            )}
          </td>
          <td className="ProjectTable-descriptionCell">
            {canEditDescription ? (
              <span
                className="ProjectTable-editable ProjectTable-descriptionTrigger"
                onClick={() => this.toggleDescriptionEditor(mission)}
                title={this.t("clickToEdit")}
              >
                {previewText || "—"}
              </span>
            ) : (
              <span>{previewText || "—"}</span>
            )}
          </td>
          <td className="ProjectTable-imageCell">
            {canEditDescription ? (
              <span
                className="ProjectTable-imageTrigger"
                onClick={() => this.openImagePicker(mission)}
                title={this.t("clickToEdit")}
              >
                {images.length > 0 ? (
                  <img
                    className="ProjectTable-imageThumb"
                    src={images[0]}
                    alt={getProjectTitle(this.props.language, mission)}
                  />
                ) : (
                  <span className="ProjectTable-imagePlaceholder">
                    {this.t("addImage")}
                  </span>
                )}
              </span>
            ) : images.length > 0 ? (
              <img
                className="ProjectTable-imageThumb"
                src={images[0]}
                alt={getProjectTitle(this.props.language, mission)}
              />
            ) : (
              <span>—</span>
            )}
          </td>
          <td className="ProjectTable-qrCell">
            {showQr ? (
              <span
                className="ProjectTable-qrTrigger"
                onClick={() => this.openQrView(mission)}
                title={this.t("clickToEdit")}
              >
                <QRCodeCanvas
                  className="ProjectTable-qrThumb"
                  value={projectUrl(this.props.subDomain, mission.id)}
                  size={48}
                />
              </span>
            ) : (
              <span>—</span>
            )}
          </td>
          <td>
            <span className="ProjectTable-statusWrap">
              <span
                className="ProjectTable-statusDot"
                style={{
                  background:
                  STATUS_COLORS[mission.status] || STATUS_COLORS.open,
                }}
              />
              <select
                className="ProjectTable-statusSelect"
                value={mission.status || "open"}
                onChange={(e) => this.handleRowStatusChange(mission, e)}
                disabled={saving || bulkSaving}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {this.statusLabel(s)}
                  </option>
                ))}
              </select>
              {savingIds[mission.id] === "status" ? (
                <span className="ProjectTable-spinner" />
              ) : null}
            </span>
          </td>
          {SHOW_TYPE ? (
            <td>
              <span className="ProjectTable-statusWrap">
                <select
                  className="ProjectTable-statusSelect"
                  value={mission.donation ? "donation" : "payment"}
                  onChange={(e) => this.handleRowTypeChange(mission, e)}
                  disabled={saving || bulkSaving}
                  aria-label={this.t("thType")}
                >
                  <option value="donation">{this.t("typeDonation")}</option>
                  <option value="payment">{this.t("typePayment")}</option>
                </select>
                {savingIds[mission.id] === "donation" ? (
                  <span className="ProjectTable-spinner" />
                ) : null}
              </span>
            </td>
          ) : null}
          <td className="ProjectTable-numCell">
            {this.renderEditableCell(
              mission,
              "targetAmount",
              formatAmount(mission.targetAmount)
            )}
          </td>
          <td className="ProjectTable-numCell">
            {formatAmount(mission.currentAmount)}
          </td>
          <td className="ProjectTable-numCell">
            {mission.donorCount != null ? mission.donorCount : "—"}
          </td>
          <td className="ProjectTable-numCell">
            {mission.transactionCount != null ? mission.transactionCount : "—"}
          </td>
          <td className="ProjectTable-numCell">
            {formatAmount(mission.averageDonation)}
          </td>
          <td>{formatDate(mission.firstDonation)}</td>
          <td>{formatDate(mission.lastDonation)}</td>
          <td className="ProjectTable-editCell">
            <button
              type="button"
              className="ProjectTable-editBtn"
              onClick={() => this.props.onEditClick(mission)}
              title={this.t("thEdit")}
            >
              ✎
            </button>
          </td>
        </tr>
        {descriptionEditId === mission.id ? (
          <tr className="ProjectTable-descriptionRow">
            <td colSpan={columnCount}>
              <ProjectDescriptionEditor
                mission={mission}
                language={this.props.language}
                languageObj={this.props.languageObj}
                langLabel={this.langLabel}
                onClose={this.closeDescriptionEditor}
                onSave={this.props.onFieldSave}
              />
            </td>
          </tr>
        ) : null}
      </React.Fragment>
    );
  }

  render() {
    const { missions, isLoading } = this.props;
    const allSelected =
      missions.length > 0 &&
      missions.every((m) => this.state.selectedIds[m.id]);

    return (
      <div className="ProjectTable">
        <div className="ProjectTable-toolbar">
          {this.props.onManageImages ? (
            <button
              type="button"
              className="ProjectTable-manageImagesBtn"
              onClick={this.props.onManageImages}
            >
              {this.props.manageImagesLabel || "Manage Images"}
            </button>
          ) : null}
          <ExportMenu
            projectIds={this.selectedIdList()}
            languageObj={this.props.languageObj}
            disabled={this.selectedIdList().length === 0}
            alignRight
          />
        </div>

        {this.renderBulkBar()}

        {this.state.error ? (
          <div className="ProjectTable-error">{this.state.error}</div>
        ) : null}

        <div
          className="ProjectTable-tableWrap"
          ref={this.tableWrapRef}
          onScroll={this.handleTableScroll}
        >
          <table
            className="ProjectTable-table"
            style={{
              width:
                OTHER_COLUMNS_WIDTH +
                this.state.titleColWidth +
                this.state.descriptionColWidth,
            }}
          >
            <colgroup>
              <col className="ProjectTable-checkCol" />
              <col style={{ width: this.state.titleColWidth }} />
              <col style={{ width: this.state.descriptionColWidth }} />
              <col className="ProjectTable-imageCol" />
              <col className="ProjectTable-qrCol" />
              <col className="ProjectTable-statusCol" />
              {SHOW_TYPE ? <col className="ProjectTable-typeCol" /> : null}
              <col className="ProjectTable-targetCol" />
              <col className="ProjectTable-raisedCol" />
              <col className="ProjectTable-donorsCol" />
              <col className="ProjectTable-transactionsCol" />
              <col className="ProjectTable-avgCol" />
              <col className="ProjectTable-firstDonationCol" />
              <col className="ProjectTable-lastDonationCol" />
              <col className="ProjectTable-editCol" />
            </colgroup>
            <thead>
              <tr>
                <th className="ProjectTable-checkCell">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={this.toggleAll}
                    aria-label="select all"
                  />
                </th>
                <th className="ProjectTable-titleCell">
                  {this.t("thTitle")}
                  <span
                    className="ProjectTable-colResizeHandle"
                    onMouseDown={(e) => this.handleResizeStart("title", e)}
                    onTouchStart={(e) => this.handleResizeStart("title", e)}
                  />
                </th>
                <th className="ProjectTable-descriptionCell">
                  {this.t("thDescription")}
                  <span
                    className="ProjectTable-colResizeHandle"
                    onMouseDown={(e) => this.handleResizeStart("description", e)}
                    onTouchStart={(e) => this.handleResizeStart("description", e)}
                  />
                </th>
                <th>{this.t("thImage")}</th>
                <th>{this.t("thQrCode")}</th>
                <th>{this.t("thStatus")}</th>
                {SHOW_TYPE ? <th>{this.t("thType")}</th> : null}
                <th className="ProjectTable-numCell">{this.t("thTarget")}</th>
                <th className="ProjectTable-numCell">{this.t("thRaised")}</th>
                <th className="ProjectTable-numCell">{this.t("thDonorCount")}</th>
                <th className="ProjectTable-numCell">{this.t("thTransactionCount")}</th>
                <th className="ProjectTable-numCell">{this.t("thAverageDonation")}</th>
                <th>{this.t("thFirstDonation")}</th>
                <th>{this.t("thLastDonation")}</th>
                <th className="ProjectTable-editCell" />
              </tr>
            </thead>
            <tbody>
              {missions.length > WINDOW_THRESHOLD
                ? this.renderWindowedBody(missions)
                : missions.map((m) => this.renderRow(m))}
            </tbody>
          </table>

          {isLoading ? (
            <div className="ProjectTable-message">{this.t("loading")}</div>
          ) : null}

          {!isLoading && missions.length === 0 ? (
            <div className="ProjectTable-message">{this.t("empty")}</div>
          ) : null}
        </div>

        {this.state.imagePickerId ? (
          <MediaGalleryModal
            mode="picker"
            title={this.t("imagePickerTitle")}
            languageObj={this.props.languageObj}
            onClose={this.closeImagePicker}
            onSelect={(url) => {
              const mission = missions.find(
                (m) => m.id === this.state.imagePickerId
              );
              if (mission) this.handleImageSelect(mission, url);
            }}
          />
        ) : null}

        {this.state.qrViewId ? (() => {
          const mission = missions.find((m) => m.id === this.state.qrViewId);
          if (!mission) return null;
          const url = projectUrl(this.props.subDomain, mission.id);
          return (
            <PaymentMethodModal
              isOpen
              onClose={this.closeQrView}
              title={this.t("thQrCode")}
              textObj={{ close: this.t("cancel") }}
            >
              <div className="ProjectTable-qrModalContent">
                <QRCodeCanvas value={url} size={200} />
                <button
                  type="button"
                  className="ProjectTable-applyBtn"
                  onClick={() => window.open(url, "_blank")}
                >
                  {this.t("viewPage")}
                </button>
              </div>
            </PaymentMethodModal>
          );
        })() : null}
      </div>
    );
  }
}

ProjectTable.propTypes = {
  missions: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  language: PropTypes.string,
  subDomain: PropTypes.string,
  languageObj: PropTypes.object,
  canUseMultiLanguage: PropTypes.bool,
  onStatusChange: PropTypes.func.isRequired,
  onBulkUpdate: PropTypes.func.isRequired,
  onFieldSave: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onProjectClick: PropTypes.func,
  onManageImages: PropTypes.func,
  manageImagesLabel: PropTypes.string,
};

export default ProjectTable;
